'use client';

import { useEffect, useState } from 'react';
import { Card, Eyebrow } from '../../_components/primitives';
import { fetchRoundStats } from '../lib/api';
import type { RoundStats, ScoreBucket } from '../lib/types';

const MOCK_STATS: RoundStats = {
  total_players: 312,
  completed: 312,
  avg_score: 6.8,
  distribution: [
    { score: 9, count: 18 },
    { score: 8, count: 41 },
    { score: 7, count: 58 },
    { score: 6, count: 54 },
    { score: 5, count: 49 },
    { score: 4, count: 38 },
    { score: 3, count: 28 },
    { score: 2, count: 14 },
    { score: 1, count: 8 },
    { score: 0, count: 4 },
  ],
};

function bucketize(distribution: ScoreBucket[]): ScoreBucket[] {
  // Group 0-4 into a single "≤4" bucket (represented as score=4)
  let lowCount = 0;
  for (const b of distribution) {
    if (b.score < 5) lowCount += b.count;
  }
  const ordered = [9, 8, 7, 6, 5].map((s) => ({
    score: s,
    count: distribution.find((b) => b.score === s)?.count ?? 0,
  }));
  return [...ordered, { score: 4, count: lowCount }];
}

function percentileBelow(
  distribution: ScoreBucket[],
  yourScore: number,
  totalCompleted: number,
): number {
  if (totalCompleted === 0) return 0;
  const below = distribution
    .filter((b) => b.score < yourScore)
    .reduce((sum, b) => sum + b.count, 0);
  return Math.round((below / totalCompleted) * 100);
}

export function TodayDistribution({
  roundId,
  yourScore = null,
  useMock = false,
}: {
  roundId?: string;
  yourScore?: number | null;
  useMock?: boolean;
}) {
  const [stats, setStats] = useState<RoundStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMock) {
      setStats(MOCK_STATS);
      setLoading(false);
      return;
    }
    if (!roundId) {
      setLoading(false);
      return;
    }
    fetchRoundStats(roundId)
      .then((s) => setStats(s))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [roundId, useMock]);

  if (loading) return null;
  if (!stats?.distribution || stats.distribution.length === 0) return null;

  const totalCompleted = stats.distribution.reduce((s, b) => s + b.count, 0);
  if (totalCompleted === 0) return null;

  const grouped = bucketize(stats.distribution);
  const max = Math.max(...grouped.map((b) => b.count), 1);
  const yourBucketScore =
    yourScore !== null && yourScore < 5 ? 4 : yourScore;
  const beatPct =
    yourScore !== null
      ? percentileBelow(stats.distribution, yourScore, totalCompleted)
      : null;

  return (
    <Card padded={false}>
      <div style={{ padding: '16px 20px 18px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 12,
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <Eyebrow tone="denim">Today · score distribution</Eyebrow>
          <span
            className="mono"
            style={{ fontSize: 11, color: 'var(--fg-4)' }}
          >
            {totalCompleted} {totalCompleted === 1 ? 'player' : 'players'}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {grouped.map((b) => (
            <DistributionRow
              key={b.score}
              label={b.score === 4 ? '≤4' : `${b.score}`}
              count={b.count}
              pct={b.count / max}
              highlighted={
                yourBucketScore !== null && b.score === yourBucketScore
              }
            />
          ))}
        </div>
        {beatPct !== null && (
          <p
            style={{
              marginTop: 14,
              paddingTop: 12,
              borderTop: '1px dashed var(--border)',
              fontFamily: 'var(--font-sans)',
              fontSize: 12,
              color: 'var(--fg-3)',
            }}
          >
            You beat{' '}
            <span style={{ color: 'var(--coral-deep)', fontWeight: 600 }}>
              {beatPct}%
            </span>{' '}
            of players today.
            {stats.avg_score !== null && (
              <>
                {' '}
                Average score ·{' '}
                <span className="mono">{stats.avg_score}</span>
              </>
            )}
          </p>
        )}
      </div>
    </Card>
  );
}

function DistributionRow({
  label,
  count,
  pct,
  highlighted,
}: {
  label: string;
  count: number;
  pct: number;
  highlighted: boolean;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '28px minmax(0, 1fr) 32px',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11.5,
          fontWeight: 500,
          color: highlighted ? 'var(--coral-deep)' : 'var(--fg-3)',
          textAlign: 'right',
        }}
      >
        {label}
      </span>
      <div
        className="mo-bg-card-soft"
        style={{
          position: 'relative',
          height: 18,
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid var(--border-soft)',
        }}
      >
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            width: `${pct * 100}%`,
            background: highlighted
              ? 'var(--coral-deep)'
              : 'var(--denim-200)',
          }}
        />
      </div>
      <span
        className="mono"
        style={{
          fontSize: 11,
          color: highlighted ? 'var(--coral-deep)' : 'var(--fg-4)',
          textAlign: 'right',
          fontWeight: highlighted ? 600 : 400,
        }}
      >
        {count}
      </span>
    </div>
  );
}
